import { useCallback, useEffect, useMemo, useState } from "react"
import { GasFeeData, SupportedTokenName } from "@mezo/sdk"
import { PayloadAction } from "@reduxjs/toolkit"
import { formatUnits } from "ethers"

import {
  getGasFeeData,
  RootState,
  selectDepositActionDepositId,
  selectDepositActionId,
  selectDepositActionStatus,
  selectDepositActionType,
  selectPortalStatistics,
  selectTokenDecimals,
  setActionStatus,
  useDappDispatch,
  useDappSelector,
  withdrawBitcoin,
  withdrawToken,
} from "#/redux-state"
import {
  PORTAL_WITHDRAWAL_GAS_USAGE,
  TBTC_BRIDGE_GAS_USAGE,
  THRESHOLD_FEE,
} from "#/shared/constants"
import { convertDecimalsToTokenDecimals } from "#/shared/utils"

import { useFetchWalletBalances } from "./wallet"

export const useWithdrawalForm = (
  customDestination: string | null,
  token: SupportedTokenName,
  isBitcoinDeposit: boolean,
  onSuccess: () => void,
) => {
  const [isProcessing, setIsProcessing] = useState(false)
  const actionType = useDappSelector(selectDepositActionType)
  const actionStatus = useDappSelector(selectDepositActionStatus)

  const depositDatabaseId = useDappSelector(selectDepositActionId)
  const depositId = useDappSelector(selectDepositActionDepositId)

  const tokenDecimals = useDappSelector((state: RootState) =>
    selectTokenDecimals(state, token),
  )

  const { fetchWalletBalances } = useFetchWalletBalances()

  const isProcessingOtherWithdrawal =
    !isProcessing &&
    (actionStatus === "processing" || actionStatus === "in-progress")

  const dispatch = useDappDispatch()

  const withdrawalHandler = useCallback(
    async (amount: bigint) => {
      if (!depositDatabaseId || !depositId || !customDestination) return

      setIsProcessing(true)

      let success = false

      if (token === "BTC") {
        const { payload } = (await dispatch(
          withdrawBitcoin({
            id: depositDatabaseId,
            depositId: BigInt(depositId),
            bitcoinRedeemerAddress: customDestination,
            amount,
            shouldOnlyRequestRedemption: actionType === "redemption",
          }),
        )) as PayloadAction<boolean>

        success = payload ?? false
      } else {
        const { payload } = (await dispatch(
          withdrawToken({
            isBitcoinDeposit,
            id: depositDatabaseId,
            depositId: BigInt(depositId),
            token,
            amount: convertDecimalsToTokenDecimals(amount, tokenDecimals),
          }),
        )) as PayloadAction<boolean>

        success = payload ?? false
      }

      setIsProcessing(false)

      if (success) {
        await fetchWalletBalances({ fetchOnChainData: true, refetch: true })
        onSuccess()
      } else {
        dispatch(setActionStatus("error"))
      }
    },
    [
      depositDatabaseId,
      depositId,
      dispatch,
      onSuccess,
      token,
      customDestination,
      isBitcoinDeposit,
      actionType,
      tokenDecimals,
      fetchWalletBalances,
    ],
  )

  return {
    withdraw: withdrawalHandler,
    isProcessing,
    isProcessingOtherWithdrawal,
  }
}

export const useWithdrawalAddress = (destinationAddress: string | null) => {
  const [customDestination, setCustomDestination] = useState<string | null>(
    destinationAddress,
  )

  const [isAddressInputVisible, setIsAddressInputVisible] = useState(false)

  const saveAddressHandler = useCallback((value: string) => {
    setCustomDestination(value)
    setIsAddressInputVisible(false)
  }, [])

  return {
    customDestination,
    isAddressInputVisible,
    setIsAddressInputVisible,
    saveAddress: saveAddressHandler,
  }
}

export const useWithdrawalFee = (amount: string, token: SupportedTokenName) => {
  const [gasPrice, setGasPrice] = useState(0n)
  const [maxPriorityFeePerGas, setMaxPriorityFeePerGas] = useState(0n)
  const statistics = useDappSelector(selectPortalStatistics)

  const dispatch = useDappDispatch()

  useEffect(() => {
    const fetchGasPrice = async () => {
      const { payload: gasFeeData } = (await dispatch(
        getGasFeeData(),
      )) as PayloadAction<GasFeeData | undefined>

      setGasPrice(gasFeeData?.gasPrice ?? 0n)
      setMaxPriorityFeePerGas(gasFeeData?.maxPriorityFeePerGas ?? 0n)
    }

    fetchGasPrice()
  }, [dispatch])

  const { btcPrice, ethPrice } = statistics ?? { btcPrice: 0, ethPrice: 0 }

  // ERC20 withdrawal cost converted to USD price
  const ethereumWithdrawalFee =
    PORTAL_WITHDRAWAL_GAS_USAGE *
    Number(formatUnits(gasPrice + maxPriorityFeePerGas, "ether")) *
    ethPrice

  // tBTC bridge gas usage converted to USD price
  const tbtcBridgeFee =
    TBTC_BRIDGE_GAS_USAGE *
    Number(formatUnits(gasPrice + maxPriorityFeePerGas, "ether")) *
    ethPrice

  // 0.2% Threshold withdrawal fee converted to USD price
  const thresholdWithdrawalFeeInBTC = Number(amount) * THRESHOLD_FEE
  const thresholdWithdrawalFee = thresholdWithdrawalFeeInBTC * btcPrice

  const withdrawalFee = useMemo(() => {
    const bitcoinWithdrawalFee =
      thresholdWithdrawalFee + ethereumWithdrawalFee + tbtcBridgeFee

    const fee = token === "BTC" ? bitcoinWithdrawalFee : ethereumWithdrawalFee

    return Number.isNaN(fee) ? null : fee.toFixed(2)
  }, [token, thresholdWithdrawalFee, ethereumWithdrawalFee, tbtcBridgeFee])

  return { withdrawalFee, thresholdWithdrawalFeeInBTC }
}
